import { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { ReactElement, ReactNode, RefObject, Dispatch, SetStateAction } from 'react';
import classNames from 'classnames';
import sharedVariables from '../../data/shared-variable/shared-variables.json';

export type ThemeColor = {
  background: string;
  foreground: string;
};

export interface ThemeColorProviderContextType {
  registerBlock(
    ref: RefObject<HTMLElement | null>,
    useAccent: boolean,
    accentColor?: ThemeColor,
  ): void;
  removeBlock(ref: RefObject<HTMLElement | null>): void;

  primaryThemeColor: RefObject<ThemeColor | null>;
  accentThemeColor: RefObject<ThemeColor | null>;

  setPrimaryThemeColor: (background?: string, foreground?: string) => void;
  setAccentThemeColor: (theme?: ThemeColor) => void;
  setDarkTheme: (isDarkTheme: boolean) => void;
  isDarkTheme: boolean;

  themeWrapperRef: RefObject<HTMLDivElement | HTMLElement | null>;
  blocks: Set<ThemeColorBlock>;

  forceRefresh: number;
  setForceRefresh: Dispatch<SetStateAction<number>>;
}

export type ThemeColorBlock = {
  ref: RefObject<HTMLElement | null>;
  useAccent: boolean;
  accentColor?: ThemeColor;
};

const ThemeColorProviderContext = createContext<ThemeColorProviderContextType | undefined>(
  undefined,
);

export function useThemeColorProviderContext(): ThemeColorProviderContextType | undefined {
  return useContext(ThemeColorProviderContext);
}

export interface ThemeColorProviderProps {
  children: ReactNode;
  accentThemeColor?: ThemeColor;
  isDarkTheme: boolean;
  useDocumentElement: boolean;
}

export const themeDark = {
  background: sharedVariables.primaryColors.background,
  foreground: sharedVariables.primaryColors.foreground,
};

export const themeLight = {
  background: sharedVariables.primaryColors.foreground,
  foreground: sharedVariables.primaryColors.background,
};

export function ThemeColorProvider(props: ThemeColorProviderProps): ReactElement {
  const body = useRef<HTMLElement>(typeof window !== 'undefined' ? document.documentElement : null);
  const themeWrapperRef = useRef<HTMLDivElement>(null);
  const darkTheme = useRef(props.isDarkTheme);
  const blocks = useRef(new Set<ThemeColorBlock>());

  const [forceRefresh, setForceRefresh] = useState(0);

  const primaryThemeColor = useRef<ThemeColor>(darkTheme ? themeDark : themeLight);
  const accentThemeColor = useRef<ThemeColor>(
    props.accentThemeColor || props.isDarkTheme ? themeLight : themeDark,
  );

  const setDarkTheme = useCallback(
    (isDarkTheme: boolean) => {
      darkTheme.current = isDarkTheme;
      primaryThemeColor.current = isDarkTheme ? themeDark : themeLight;
    },
    [darkTheme],
  );

  const setPrimaryThemeColor = useCallback(
    (background?: string, foreground?: string) => {
      if (background && foreground && background !== '#f40000' && foreground !== '#f40000') {
        primaryThemeColor.current = {
          background,
          foreground,
        };
        return;
      }

      primaryThemeColor.current = darkTheme.current ? themeDark : themeLight;
    },
    [primaryThemeColor, darkTheme],
  );

  const setAccentThemeColor = useCallback(
    (theme?: ThemeColor) => {
      if (theme) {
        accentThemeColor.current = theme;
      } else {
        accentThemeColor.current = darkTheme.current ? themeLight : themeDark;
      }
    },
    [accentThemeColor, darkTheme],
  );

  const removeBlock = useCallback(
    (ref: RefObject<HTMLElement | null>) => {
      const match = Array.from(blocks.current).find((block) => block.ref === ref);
      if (match) {
        blocks.current.delete(match);
      }
    },
    [blocks],
  );

  const registerBlock = useCallback(
    (ref: RefObject<HTMLElement | null>, useAccent: boolean, accentColor: ThemeColor) => {
      blocks.current.add({ ref, accentColor, useAccent: !!useAccent });
    },
    [blocks],
  );

  return (
    <ThemeColorProviderContext.Provider
      value={{
        primaryThemeColor,
        accentThemeColor,
        registerBlock,
        removeBlock,
        blocks: blocks.current,
        setPrimaryThemeColor,
        setAccentThemeColor,
        setDarkTheme,
        isDarkTheme: darkTheme.current,
        themeWrapperRef: props.useDocumentElement ? body : themeWrapperRef,
        forceRefresh,
        setForceRefresh,
      }}
    >
      {!props.useDocumentElement && (
        <div ref={themeWrapperRef} className={classNames('theme-color')}>
          {props.children}
        </div>
      )}
      {props.useDocumentElement && props.children}
    </ThemeColorProviderContext.Provider>
  );
}
